import styles from './Hero5050Component.module.scss';
import { Link, Button } from '~/shared/components';
import { Hero5050Props, VideoMedia } from '~/lib/data-contract/auto-generated/api-model';
import Typography, { TypographyStyle } from '~/shared/components/Typography/Typography';
import { Watermark } from '~/shared/components/Watermarks';
import clsx from 'clsx';
import { useMemo } from 'react';
import { BLACK_FRIDAY_THEME } from '~/theme/constants';
import { useButtonVariant } from '~/shared/components/Button/useButtonVariant';
import { defaultColorTheme } from '~/shared/utils/defaultColorTheme';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';

type Hero5050ComponentProps = Hero5050Props & {
    media: React.ReactNode;
    countDownTimer?: React.ReactNode;
    countDownEnds?: boolean;
    video?: VideoMedia;
};

const Hero5050Component = ({
    trumpet,
    title,
    paragraphText,
    imageOnBackground,
    colorTheme,
    watermark,
    link,
    disclaimer,
    titleSize,
    linkType,
    reverseContent,
    aboveTheFold,
    media,
    countDownTimer,
    countDownEnds,
}: Hero5050ComponentProps) => {
    const titleVariant: TypographyStyle = useMemo(() => {
        switch (titleSize) {
            case 'Medium':
                return 'title-L';
            case 'Large':
                return 'display-M';
            case 'Extra Large':
                return 'display-XL';
            default:
                return 'title-L';
        }
    }, [titleSize]);

    const buttonVariant = useButtonVariant(
        linkType ?? 'Default',
        colorTheme?.foregroundColor || defaultColorTheme?.foregroundColor,
    );

    const {
        mainColor,
        highlightColor,
        foregroundColor,
        secondaryColor,
        secondaryColorOpacity,
        gradientHighlight,
        textColor,
    } = colorTheme || defaultColorTheme;

    return (
        <section
            className={styles.component}
            style={
                {
                    '--shadow-color': highlightColor,
                    '--background-color': mainColor,
                    '--watermark-color': secondaryColor,
                    '--watermark-opacity': secondaryColorOpacity,
                    '--color-text': textColor,
                    '--bullet-color': mainColor === '#f68a20' ? foregroundColor : '#f68a20',
                    color: foregroundColor,
                } as React.CSSProperties
            }
        >
            <ModuleContainer
                hasGutter={!aboveTheFold}
                spacingTop={aboveTheFold ? 'none' : undefined}
            >
                <div
                    className={clsx(styles.wrapper, {
                        [styles.reversed]: reverseContent,
                    })}
                >
                    <div className={styles.bg}>
                        {!!watermark && !imageOnBackground && (
                            <Watermark
                                type={watermark}
                                styles={{
                                    watermarkLogo: styles.markLogo,
                                    watermarkPercent: styles.markPercentage,
                                }}
                            />
                        )}
                    </div>
                    <div className={styles.textContainer}>
                        <div className={styles.textInner}>
                            {trumpet && (
                                <Typography as="p" variant="trumpet-XL" className={styles.trumpet}>
                                    {trumpet}
                                </Typography>
                            )}

                            {title && (
                                <Typography variant={titleVariant} className={styles.title} as="h2">
                                    {title}
                                </Typography>
                            )}

                            {paragraphText && (
                                <Typography
                                    className={clsx(styles.rte, styles.paragraph)}
                                    variant="paragraph-L"
                                    as="div"
                                    dangerouslySetInnerHTML={{ __html: paragraphText }}
                                />
                            )}

                            {countDownTimer}

                            {countDownTimer && countDownEnds && link?.url && (
                                <Button
                                    className={clsx(styles.button, 'hoverShadowStyle')}
                                    as={Link}
                                    aria-label="link"
                                    variant={buttonVariant}
                                    href={link.url}
                                    target={link.target}
                                    isInverted={
                                        foregroundColor === '#ffffff' ||
                                        foregroundColor === BLACK_FRIDAY_THEME
                                    }
                                >
                                    {link.title}
                                </Button>
                            )}

                            {!countDownTimer && link?.url && (
                                <Button
                                    className={clsx(styles.button, 'hoverShadowStyle')}
                                    as={Link}
                                    aria-label="link"
                                    variant={buttonVariant}
                                    href={link.url}
                                    target={link.target}
                                    isInverted={
                                        foregroundColor === '#ffffff' ||
                                        foregroundColor === BLACK_FRIDAY_THEME
                                    }
                                >
                                    {link.title}
                                </Button>
                            )}
                        </div>
                    </div>
                    <div
                        className={clsx(styles.media, {
                            [styles.isAbovefold]: aboveTheFold,
                        })}
                    >
                        <div
                            className={clsx(
                                gradientHighlight ? styles.shadowGradient : styles.shadow,
                            )}
                        />

                        <figure
                            className={clsx(styles.imageContainer, {
                                [styles.imageOnBackground]: imageOnBackground,
                            })}
                        >
                            {watermark && imageOnBackground && (
                                <Watermark
                                    type={watermark}
                                    styles={{
                                        watermarkLogo: styles.markLogo,
                                        watermarkPercent: styles.markPercentage,
                                    }}
                                />
                            )}

                            {media}
                        </figure>
                    </div>
                </div>

                {disclaimer && (
                    <Typography
                        variant="disclaimer-M"
                        as="div"
                        className={clsx(styles.disclaimer, {
                            [styles.disclaimerReversed]: reverseContent,
                        })}
                        dangerouslySetInnerHTML={{ __html: disclaimer }}
                    />
                )}
            </ModuleContainer>
        </section>
    );
};

export default Hero5050Component;
